import { useState, useEffect, useCallback } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppLayout, type PageId } from "@/components/AppLayout";
import { DashboardPage } from "@/pages/DashboardPage";
import { UsersPage } from "@/pages/UsersPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { LoginForm, ForgotPasswordForm } from "@/pages/LoginPage";
import { SignupForm } from "@/pages/SignupPage";
import { Card, CardContent } from "@/components/ui/card";
import logoUrl from "@/assets/logo.svg";
import { LinkGooglePage } from "@/pages/LinkGooglePage";
import { LinkGoogleSuccessPage } from "@/pages/LinkGoogleSuccessPage";
import { SetPasswordPage } from "@/pages/SetPasswordPage";
import "./index.css";

function AppContent() {
  const { user, loading, refresh } = useAuth();
  const [page, setPage] = useState<PageId>("dashboard");
  const [authTab, setAuthTab] = useState<"login" | "signup" | "forgot">("login");

  const handleNavigate = useCallback((p: PageId) => setPage(p), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") !== "success") return;
    window.history.replaceState({}, "", window.location.pathname);
    refresh();
    const t1 = setTimeout(refresh, 2000);
    const t2 = setTimeout(refresh, 5000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [refresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">読み込み中…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md flex flex-col items-center gap-6">
          <img
            src={logoUrl}
            alt="Gyoai"
            className="h-9 w-auto object-contain"
          />
          <Card className="w-full border-border/80 bg-card/50 overflow-hidden">
            <div className="flex border-b border-border">
              <button
                type="button"
                onClick={() => setAuthTab("login")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  authTab === "login"
                    ? "bg-muted/80 text-foreground border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                ログイン
              </button>
              <button
                type="button"
                onClick={() => setAuthTab("signup")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  authTab === "signup"
                    ? "bg-muted/80 text-foreground border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                登録
              </button>
              <button
                type="button"
                onClick={() => setAuthTab("forgot")}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  authTab === "forgot"
                    ? "bg-muted/80 text-foreground border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                パスワード再設定
              </button>
            </div>
            <CardContent className="pt-6">
              {authTab === "login" && <LoginForm />}
              {authTab === "signup" && <SignupForm />}
              {authTab === "forgot" && <ForgotPasswordForm />}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AppLayout page={page} onNavigate={handleNavigate}>
      {page === "dashboard" && <DashboardPage />}
      {page === "users" && <UsersPage />}
      {page === "settings" && <SettingsPage />}
    </AppLayout>
  );
}

export function App() {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (pathname === "/link-google/success") return <LinkGoogleSuccessPage />;
  if (pathname === "/link-google") return <LinkGooglePage />;
  if (pathname === "/set-password") return <SetPasswordPage />;
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
